import { ReactNode, useEffect, useMemo, useState } from "react";
import { v4 } from "uuid";
import { Plus, Trash2 } from "lucide-react";
import { useNavigate } from "react-router";
import t from "@/lang/lang";
import {
  useGfsCatalogDeleteMutation,
  useGfsCatalogListQuery,
  useGfsCatalogPutMutation,
} from "@/services/backend/gfs/catalog/api";
import { GfsCatalogListItem } from "@/services/backend/gfs/catalog/types";
import { useActiveCompany } from "@/shared/lib/active-company/active-company-provider";
import {
  DataTable,
  DataTableContainer,
  useDataTableController,
} from "@/shared/components/tables/data-table";
import { ColumnDef } from "@tanstack/react-table";
import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { formatDate } from "@/shared/lib/utilities/date";
import { NullUUID } from "@/shared/lib/utilities/uuid";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { CustomerSelect } from "@/services/backend/customers/components/customer-select";
import { CustomerName } from "@/services/backend/customers/components/customer-name";
import { PersistenceState } from "@/shared/lib/persistence-state/persistence-state";
import { PersistenceStateInfo } from "@/shared/lib/persistence-state/persistence-state-info";

export function GfsCatalogList() {
  const { companyId } = useActiveCompany();
  const { data, error, isLoading } = useGfsCatalogListQuery({
    company: { active: true, values: [companyId] },
    limit: 100,
    offset: 0,
    orderBy: [{ field: "name" }],
  });
  const [deleteCatalog, deleteState] = useGfsCatalogDeleteMutation();
  const navigate = useNavigate();
  const rows = data?.items ?? [];
  const globalCatalogs = rows.filter((catalog) => !catalog.customerId);
  const customerCatalogs = rows.filter((catalog) => catalog.customerId);

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  return (
    <div className="grid gap-4">
      <StandardCatalogBlock companyId={companyId} catalogs={globalCatalogs} />
      <CatalogSection
        title={t("Globale Kataloge")}
        description={t(
          "Diese Kataloge können ohne Kundenbezug verwendet werden.",
        )}
        catalogs={globalCatalogs}
        isLoading={isLoading}
        onDelete={(id) => deleteCatalog({ id })}
        onOpen={(id) => navigate(id)}
        actions={<CreateCatalogDialog companyId={companyId} />}
      />
      <CatalogSection
        title={t("Kundenkataloge")}
        description={t("Diese Kataloge sind einem Kunden zugeordnet.")}
        catalogs={customerCatalogs}
        isLoading={isLoading}
        showCustomer
        onDelete={(id) => deleteCatalog({ id })}
        onOpen={(id) => navigate(id)}
        actions={
          <CreateCatalogDialog
            companyId={companyId}
            requireCustomer
            triggerLabel={t("Kundenkatalog anlegen")}
          />
        }
      />
      {deleteState.error && <RTKQueryErrorAlert error={deleteState.error} />}
    </div>
  );
}

function CatalogSection({
  title,
  description,
  catalogs,
  isLoading,
  showCustomer = false,
  actions,
  onOpen,
  onDelete,
}: {
  title: string;
  description: string;
  catalogs: GfsCatalogListItem[];
  isLoading: boolean;
  showCustomer?: boolean;
  actions: ReactNode;
  onOpen: (id: string) => void;
  onDelete: (id: string) => void;
}) {
  const columns = useMemo<ColumnDef<GfsCatalogListItem>[]>(
    () => [
      {
        accessorKey: "name",
        header: t("Name"),
        cell: ({ row }) => (
          <button
            className="text-primary font-medium hover:underline"
            onClick={() => onOpen(row.original.id)}
          >
            {row.original.name}
          </button>
        ),
      },
      ...(showCustomer
        ? [
            {
              accessorKey: "customerId",
              header: t("Kunde"),
              cell: ({ row }) => (
                <CustomerName customerId={row.original.customerId} />
              ),
            } satisfies ColumnDef<GfsCatalogListItem>,
          ]
        : []),
      { accessorKey: "description", header: t("Beschreibung") },
      { accessorKey: "itemCount", header: t("Monitore") },
      {
        accessorKey: "updatedAt",
        header: t("Aktualisiert am"),
        cell: ({ row }) => formatDate(row.original.updatedAt),
      },
      {
        id: "actions",
        header: "",
        cell: ({ row }) => (
          <div className="flex justify-end">
            <Button
              size="icon"
              variant="ghost"
              onClick={() => onDelete(row.original.id)}
            >
              <Trash2 className="size-4" />
            </Button>
          </div>
        ),
      },
    ],
    [onDelete, onOpen, showCustomer],
  );
  const table = useDataTableController(columns, catalogs);

  return (
    <div className="bg-background grid gap-3 rounded-md border p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold">{title}</h3>
          <p className="text-muted-foreground text-sm">{description}</p>
        </div>
        {actions}
      </div>
      <DataTableContainer className={isLoading ? "animate-pulse" : undefined}>
        <DataTable columns={columns} table={table} />
      </DataTableContainer>
    </div>
  );
}

function StandardCatalogBlock({
  companyId,
  catalogs,
}: {
  companyId: string;
  catalogs: GfsCatalogListItem[];
}) {
  const fallback = catalogs.find((catalog) => catalog.isFallback);
  const [selectedCatalogId, setSelectedCatalogId] = useState(
    fallback?.id ?? "",
  );
  const [putCatalog, putState] = useGfsCatalogPutMutation();
  const [persistenceState, setPersistenceState] = useState(
    PersistenceState.SAVED,
  );

  useEffect(() => {
    setSelectedCatalogId(fallback?.id ?? "");
  }, [fallback?.id]);

  useEffect(() => {
    if (putState.isLoading) {
      setPersistenceState(PersistenceState.SAVING);
      return;
    }
    if (putState.error) {
      setPersistenceState(PersistenceState.ERROR);
      return;
    }
    if (putState.isSuccess) {
      setPersistenceState(PersistenceState.SAVED);
    }
  }, [putState.error, putState.isLoading, putState.isSuccess]);

  const setStandardCatalog = (catalogId: string) => {
    setSelectedCatalogId(catalogId);
    const selected = catalogs.find((catalog) => catalog.id === catalogId);
    if (!selected || selected.isFallback) {
      setPersistenceState(PersistenceState.SAVED);
      return;
    }

    setPersistenceState(PersistenceState.SAVING);
    void putCatalog({
      id: selected.id,
      companyId,
      name: selected.name,
      description: selected.description,
      isFallback: true,
    })
      .unwrap()
      .catch(() => undefined);
  };

  return (
    <div className="bg-background grid gap-3 rounded-md border p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold">{t("Standardkatalog")}</h3>
          <p className="text-muted-foreground text-sm">
            {t(
              "Dieser Katalog wird verwendet, wenn kein anderer Katalog ausgewählt wurde.",
            )}
          </p>
        </div>
        <PersistenceStateInfo state={persistenceState} />
      </div>
      <div className="flex flex-wrap items-end gap-2">
        <div className="grid min-w-[340px] gap-1.5">
          <Label>{t("Aktueller Standardkatalog")}</Label>
          <Select
            value={selectedCatalogId}
            onValueChange={setStandardCatalog}
            disabled={putState.isLoading}
          >
            <SelectTrigger>
              <SelectValue placeholder={t("Katalog auswählen")}>
                {fallback ? (
                  <CatalogSelectLabel catalog={fallback} />
                ) : undefined}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {catalogs.map((catalog) => (
                <SelectItem key={catalog.id} value={catalog.id}>
                  <CatalogSelectLabel catalog={catalog} />
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      {putState.error && <RTKQueryErrorAlert error={putState.error} />}
    </div>
  );
}

function CatalogSelectLabel({ catalog }: { catalog: GfsCatalogListItem }) {
  return (
    <span className="flex min-w-0 items-center gap-2">
      <span className="truncate font-medium">{catalog.name}</span>
      <span className="text-muted-foreground shrink-0 text-xs">
        {formatDate(catalog.updatedAt)}
      </span>
    </span>
  );
}

function CreateCatalogDialog({
  companyId,
  requireCustomer = false,
  triggerLabel = t("Katalog anlegen"),
}: {
  companyId: string;
  requireCustomer?: boolean;
  triggerLabel?: string;
}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [customerId, setCustomerId] = useState(NullUUID);
  const [putCatalog, putState] = useGfsCatalogPutMutation();

  const save = async () => {
    if (!name.trim() || (requireCustomer && customerId === NullUUID)) return;
    await putCatalog({
      id: v4(),
      companyId,
      customerId: customerId !== NullUUID ? customerId : undefined,
      name: name.trim(),
      description: description.trim(),
      isFallback: false,
    }).unwrap();
    setName("");
    setDescription("");
    setCustomerId(NullUUID);
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="size-4" />
          {triggerLabel}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("GfS-Katalog anlegen")}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4">
          {requireCustomer && (
            <div className="grid gap-1.5">
              <Label>{t("Kunde")}</Label>
              <CustomerSelect
                value={customerId}
                onValueChange={(value) => setCustomerId(value ?? NullUUID)}
              />
            </div>
          )}
          <div className="grid gap-1.5">
            <Label>{t("Name")}</Label>
            <Input
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </div>
          <div className="grid gap-1.5">
            <Label>{t("Beschreibung")}</Label>
            <Input
              value={description}
              onChange={(event) => setDescription(event.target.value)}
            />
          </div>
          {putState.error && <RTKQueryErrorAlert error={putState.error} />}
        </div>
        <DialogFooter>
          <Button
            onClick={save}
            disabled={
              !name.trim() ||
              (requireCustomer && customerId === NullUUID) ||
              putState.isLoading
            }
          >
            {t("Speichern")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
